import React, { useEffect } from "react"
import { Container, Modal } from "@carrotfertility/carotene"
import { StateMachineProvider, StateMachineStepView } from "../../context/stateMachine/StateMachineV2"
import {
  caseRateRenewalSteps,
  caseRateRenewalWorkflows,
  Steps as CaseRateRenewalSteps,
  Workflows as CaseRateRenewalWorkflows
} from "./workflow"
import {
  AdvancedCheckInEmailLinkQueryString,
  AdvancedCheckInWarningQueryString,
  CaseRateRenewalsContextProvider,
  useCaseRateRenewals
} from "./useCaseRateRenewals"
import useCarrotPlan from "../hooks/useCarrotPlan"
import {
  CaseRateRenewalsStatus,
  useGetCaseRateRenewalStatus,
  useGetDaysSinceLastUnlock,
  useSetNonStaleCarrotPlanOnAnniversaryCheck
} from "./useQueryCaseRateRenewals"
import { useHistory } from "react-router-dom"
import queryString from "query-string"
import { getHeap } from "../../../utils/heap"
import { AdvancedCheckInWarning } from "./AdvancedCheckInWarning"

function CarrotPlansGatedAccountModal(): JSX.Element {
  const {
    openGatedAccountModal,
    setOpenGatedAccountModal,
    requestError,
    disableModalCloseOnLoading
  } = useCaseRateRenewals()
  const { data, isLoading, showAdvancedCheckInFeatures } = useGetCaseRateRenewalStatus()
  const { mutate: setNonStaleCarrotPlanOnAnniversaryCheck } = useSetNonStaleCarrotPlanOnAnniversaryCheck()
  const { data: daysFromLastUnlock } = useGetDaysSinceLastUnlock()
  const history = useHistory()
  const { step } = queryString.parse(window.location.search)
  const showAdvancedCheckFromLinkInEmail =
    data === CaseRateRenewalsStatus.ADVANCED_CHECK_IN_DISMISSED &&
    (step === AdvancedCheckInEmailLinkQueryString.KEEP_PLAN ||
      step === AdvancedCheckInEmailLinkQueryString.REFRESH_PLAN)

  useEffect(() => {
    setOpenGatedAccountModal(
      data === CaseRateRenewalsStatus.GATED ||
        data === CaseRateRenewalsStatus.ADVANCED_CHECK_IN_GATED ||
        showAdvancedCheckFromLinkInEmail
    )
    if (daysFromLastUnlock >= 365) {
      setNonStaleCarrotPlanOnAnniversaryCheck(null)
    }

    if (data === CaseRateRenewalsStatus.ADVANCED_CHECK_IN_GATED || showAdvancedCheckFromLinkInEmail) {
      getHeap().track("CaseRateRenewalsAdvancedCheckIn", { EventName: "Advanced check in opened" })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- See https://carrotfertility.atlassian.net/wiki/spaces/PE/pages/2050295461/Remove+Build+Warnings#react-hooks%2Fexhaustive-deps
  }, [isLoading])

  enum Sections {
    CASE_RATE_RENEWAL = "CaseRateRenewal"
  }

  const caseRateRenewalSection = {
    name: Sections.CASE_RATE_RENEWAL,
    initialStep: showAdvancedCheckInFeatures
      ? CaseRateRenewalSteps.CARROT_PLAN_OPTIONS
      : CaseRateRenewalSteps.WELCOME_BACK_INTRO,
    desiredWorkflow: CaseRateRenewalWorkflows.CASE_RATE_RENEWAL,
    steps: caseRateRenewalSteps,
    workflows: caseRateRenewalWorkflows
  }

  return openGatedAccountModal ? (
    <StateMachineProvider
      initialSection={Sections.CASE_RATE_RENEWAL}
      stateMachineDescription={[caseRateRenewalSection]}
      onComplete={null}
    >
      <Modal
        backgroundColor={requestError ? "white" : "warmGray"}
        open={openGatedAccountModal}
        onClose={showAdvancedCheckInFeatures ? () => history.push(`/?step=${AdvancedCheckInWarningQueryString}`) : null}
        showClose={
          showAdvancedCheckInFeatures && step !== AdvancedCheckInWarningQueryString && !disableModalCloseOnLoading
        }
        fullScreenMobile
      >
        <Container padding={["medium", "medium", "huge"]}>
          {step === AdvancedCheckInWarningQueryString ? <AdvancedCheckInWarning /> : <StateMachineStepView />}
        </Container>
      </Modal>
    </StateMachineProvider>
  ) : null
}

export function CarrotPlansGatedAccountContainer(): JSX.Element {
  const { data: carrotPlan, isLoading: carrotPlanIsLoading } = useCarrotPlan()

  return !carrotPlanIsLoading && carrotPlan ? (
    <CaseRateRenewalsContextProvider>
      <CarrotPlansGatedAccountModal />
    </CaseRateRenewalsContextProvider>
  ) : null
}
